import { useEffect } from 'react';

import FilterDropdown, { FilterButton } from '../FilterDropdown';
import { useFilterContext } from '../../contexts/FilterContext';
import Header from './Header';
import {
  ANNUAL_MULTI_YEAR,
  ANNUAL_MULTI_YEAR_FILTERS,
  MULTI_YEAR,
  MULTI_YEAR_FILTERS,
  N_YEARS,
} from '../../constants/years';
import { SectionGroupsQuery } from '../../queries';
import { useQueryWithFarmId } from '../../utils/hooks';
import { captureEvent } from '../../utils/analytics';
import SectionGroupsFilterDropdown from './SectionGroupsFilterDropdown';
import SwitchInput from '../basics/SwitchInput';
import { useSubsidySwitchContext } from '../../contexts/SubsidySwitchContext';
import HeaderToggleButtons from './HeaderToggleButtons';
import GroupedYearSelect, { getOptionKey } from '../GroupedYearSelect';

const MetricsHeader = ({
  children,
  crops,
  showCropFilter,
  showCumulativeYearSelect,
  showAnnualMultiYearSelect,
  showAnnualSingleYearSelect,
  showChartButton = true,
  showTableButton = false,
}) => {
  const {
    state: { years, crop, year, sectionGroupId },
    updateFilter,
    updateSingleYearFilter,
    updateMultiYearFilter,
  } = useFilterContext();

  const { showSubsidySwitch } = useSubsidySwitchContext();
  const { subsidiesEnabled } = useFilterContext().state;

  const sectionGroups =
    useQueryWithFarmId(SectionGroupsQuery, {
      fetchPolicy: 'cache-and-network',
      context: { priorityOp: true },
    }).data?.farm.sectionGroups || [];

  const showSectionGroupsFilter = sectionGroups?.length > 0 || sectionGroupId;

  // Switching between pages with different filter options can cause the filter to break
  // So, reset the filter if the filter options change
  useEffect(() => {
    const options = [
      {
        optionIsPossible: showAnnualSingleYearSelect,
        optionIsValid: year >= 0 && year < N_YEARS,
        optionIsChosen: typeof year === 'number',
        setNewOption: () => updateSingleYearFilter(0),
      },
      {
        optionIsPossible: showCumulativeYearSelect,
        optionIsValid: MULTI_YEAR_FILTERS.includes(years),
        optionIsChosen: year === MULTI_YEAR,
        setNewOption: () => updateMultiYearFilter(MULTI_YEAR, 30),
      },
      {
        optionIsPossible: showAnnualMultiYearSelect,
        optionIsValid: ANNUAL_MULTI_YEAR_FILTERS.includes(years),
        optionIsChosen: year === ANNUAL_MULTI_YEAR,
        setNewOption: () => updateMultiYearFilter(ANNUAL_MULTI_YEAR, 30),
      },
    ];
    const chosenOption = options.find((option) => option.optionIsChosen);
    if (chosenOption?.optionIsPossible && chosenOption?.optionIsValid) return;

    // Set a valid option for the chosen option if it is possible, OR
    // Find the first possible option, set it as the new option, and return
    const newOption = chosenOption?.optionIsPossible
      ? chosenOption
      : options.find((option) => option.optionIsPossible);
    if (newOption) {
      newOption.setNewOption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showCumulativeYearSelect,
    showAnnualSingleYearSelect,
    showAnnualMultiYearSelect,
  ]);

  return (
    <Header showFarmButtons>
      <div className="d-flex align-items-center">
        <h3>{children}</h3>
      </div>

      {(showSectionGroupsFilter ||
        showAnnualSingleYearSelect ||
        showCropFilter ||
        showCumulativeYearSelect ||
        showSubsidySwitch) && (
        <div className="d-flex align-items-center">
          {showSectionGroupsFilter && (
            <SectionGroupsFilterDropdown sectionGroups={sectionGroups} />
          )}
          {showCropFilter && crops && (
            <FilterDropdown
              options={crops}
              setOption={(value) => updateFilter('crop', value)}
              selectedOption={crop}
              optionsTitle="crop"
              className="mx-2"
            />
          )}
          {(showAnnualSingleYearSelect ||
            showCumulativeYearSelect ||
            showAnnualMultiYearSelect) && (
            <GroupedYearSelect
              selectedOptionKey={getOptionKey(year, years)}
              setOption={(value) => {
                if (typeof value.year === 'number') {
                  updateSingleYearFilter(value.year);
                } else {
                  updateMultiYearFilter(value.year, value.years);
                }
              }}
              showCumulativeYearSelect={showCumulativeYearSelect}
              showAnnualMultiYearSelect={showAnnualMultiYearSelect}
              showAnnualSingleYearSelect={showAnnualSingleYearSelect}
            />
          )}
          {showSubsidySwitch && (
            <FilterButton
              size="sm"
              className="ml-2"
              selected={subsidiesEnabled}
              onClick={() => {
                const newValue = !subsidiesEnabled;
                updateFilter('subsidiesEnabled', newValue);
                captureEvent('subsidy-switch-clicked', {
                  subsidiesEnabled: newValue,
                });
              }}
            >
              <span className="mr-1">Apply subsidy</span>
              <SwitchInput
                checked={subsidiesEnabled}
                readOnly
                // Don't call parent button's onClick a second time
                onClick={(e) => e.stopPropagation()}
              />
            </FilterButton>
          )}

          <HeaderToggleButtons
            showChartButton={showChartButton}
            showTableButton={showTableButton}
          />
        </div>
      )}
    </Header>
  );
};

export default MetricsHeader;
